import type { AuthChangeEvent, User } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";

const AUTH_HYDRATION_WAIT_MS = 1000;
const AUTH_HYDRATION_EVENTS = new Set<AuthChangeEvent>([
  "INITIAL_SESSION",
  "SIGNED_IN",
  "TOKEN_REFRESHED",
  "USER_UPDATED",
  "PASSWORD_RECOVERY",
]);

const isSessionMissingError = (error: unknown) =>
  error instanceof Error &&
  (error.name === "AuthSessionMissingError" || /auth session missing|session missing/i.test(error.message));

const waitForAuthHydration = async (): Promise<User | null> =>
  new Promise((resolve) => {
    let settled = false;
    let unsubscribe = () => {};

    const finish = (user: User | null) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeoutId);
      unsubscribe();
      resolve(user);
    };

    const timeoutId = setTimeout(() => {
      finish(null);
    }, AUTH_HYDRATION_WAIT_MS);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!AUTH_HYDRATION_EVENTS.has(event)) {
        return;
      }

      finish(session?.user ?? null);
    });

    unsubscribe = () => {
      subscription.unsubscribe();
    };
  });

const resolveAuthenticatedUser = async (): Promise<User | null> => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (session?.user) {
    return session.user;
  }

  if (sessionError && !isSessionMissingError(sessionError)) {
    throw sessionError;
  }

  const hydratedUser = await waitForAuthHydration();

  if (hydratedUser) {
    return hydratedUser;
  }

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    if (isSessionMissingError(userError)) {
      return null;
    }

    throw userError;
  }

  return user ?? null;
};

export const getPlatformAdminStatus = async (): Promise<boolean> => {
  const user = await resolveAuthenticatedUser();

  if (!user) {
    return false;
  }

  const { data, error } = await supabase.rpc("get_platform_admin_status");

  if (error) {
    throw error;
  }

  return data === true;
};
